<template>
    <div class="article-overview">
        <div class="article-overview__hero" :style="{ 'background-image': 'url(' + heroBackground + ')' }">
            <div class="hero__inner container">
                <div class="hero__content">
                    <h1 class="hero__content-title" v-html="articleOverview.headline"></h1>
                    <p class="hero__content-subtitle">{{ articleOverview.headerCopy }}</p>
                </div>
                <div v-if="articleOverview.showSubscribe" class="hero__blog">
                    <p class="hero__blog-title">Subscribe to our blog</p>
                    <input type="email" autocomplete="email" class="hero__blog-input" placeholder="Email Address" aria-label="Email Address" />
                    <button class="hero__blog-signup">Sign Up</button>
                </div>
            </div>
        </div>
        <div class="article-overview__content container">
            <div v-if="articleOverview.featuredArticle" class="article-overview__featured">
                <div class="featured__image">
                    <a :href="articleOverview.featuredArticle.relativeUrl"><img class="featured__src" :src="articleOverview.featuredArticle.image" :alt="articleOverview.featuredArticle.imageAlt"></a>
                </div>
                <div class="featured__content">
                    <a :href="articleOverview.featuredArticle.relativeUrl"><h2 class="featured__title">{{ articleOverview.featuredArticle.title }}</h2></a>
                    <p v-if="articleOverview.featuredArticle.publishDate !== '0001-01-01T00:00:00' || articleOverview.featuredArticle.author || articleOverview.featuredArticle.authorTitle" class="featured__subtitle">
                        <span v-if="articleOverview.featuredArticle.publishDate !== '0001-01-01T00:00:00'">{{ formatToDate(articleOverview.featuredArticle.publishDate) }}</span>
                        <span v-if="articleOverview.featuredArticle.publishDate !== '0001-01-01T00:00:00' && articleOverview.featuredArticle.author"> / </span>
                        <span>{{ articleOverview.featuredArticle.author }}</span>
                        <span v-if="articleOverview.featuredArticle.author && articleOverview.featuredArticle.authorTitle">, </span>
                        <span>{{ articleOverview.featuredArticle.authorTitle }}</span>
                    </p>
                    <p class="featured__copy">{{ articleOverview.featuredArticle.copy }}</p>
                    <a 
                        class="featured__cta btn" 
                        :aria-label="`Read more about ${articleOverview.featuredArticle.title}`" 
                        :href="articleOverview.featuredArticle.relativeUrl">
                        Read More
                    </a>
                </div>
            </div>
            <div v-if="articleOverview.showFilters" class="article-overview__filters">
                <div class="filters__dropdown" :data-value="selectedCategory" :data-list="list">
                    <template v-if="articleOverview.shouldShowCategories">
                        <p class="filters__title">Filter by:</p>
                        <div class="selector" @click="selectVisible = !selectVisible">
                            <div class="label">
                                <span v-if="selectedCategory">{{ selectedCategory.displayName }}</span>
                                <span v-else>Category</span>
                            </div>
                            <div v-if="selectedCategory" class="close" @click="clearSelect()"></div>
                            <div class="arrow" :class="{ 'expanded' : selectVisible }"></div>
                            <div :class="{ 'hidden' : !selectVisible }">
                                <ul>
                                    <li v-for="item in articleOverview.articleCategories" :class="{ current : item === selectedCategory }" @click="selectedCategory = item">{{ item.displayName }}</li>
                                </ul>
                            </div>
                        </div>
                    </template>
                </div>
                <div v-if="articleOverview.showSearch" class="filters__search">
                    <input v-model="searchString" placeholder="Search" aria-label="Search">
                </div>
            </div>
            <div class="article-overview__articles">
                <div v-for="article in paginatedArticles" class="article-overview__article">

                    <div class="article__image">
                        <a :href="article.relativeUrl">
                            <img class="article__src" :src="article.image" :alt="article.imageAlt">
                        </a>
                    </div>
                    <div class="article__content">
                        <h2 class="article__title"><a :href="article.relativeUrl">{{ article.title }}</a></h2>
                        <p class="article__subtitle">
                            <template v-if="article.publishDate !== '0001-01-01T00:00:00'">
                                {{ formatToDate(article.publishDate) }}
                            </template>
                            <template v-if="article.publishDate !== '0001-01-01T00:00:00' && article.author">
                                /
                            </template>
                            {{ article.author }}
                        </p>
                        <span class="article__copy">{{ article.copy }}</span>
                        <a class="article__cta" 
                            :aria-label="`Read more about ${article.copy}`" 
                            :href="article.relativeUrl">
                            Read More
                        </a>
                    </div>
                </div>

            </div>
            <div class="article-overview__pagination">
                <ul class="pagination" :class="{ 'disabled': disabled}" v-show="totalPages > 1">
                    <li ref="navPrev">
                        <button class="pagination-navigation prev"
                                :class="{ 'disabled': isValueFirst }"
                                @click="goPrevious"
                                aria-label="Previous page"/>
                    </li>

                    <li v-for="(item, index) in items" :key="'paging_' + index">
                        <span v-if="isNaN(Number(item))" class="pagination-more">{{ item }}</span>
                        <button v-else
                                class="pagination-item"
                                :class="{ 'active': item === pageCurrent }"
                                @click="pageCurrent = item">
                            {{ item }}
                        </button>
                    </li>

                    <li ref="navNext">
                        <button class="pagination-navigation next"
                                :class="{ 'disabled': isValueLast }"
                                @click="goNext()"
                                aria-label="Next page"/>
                    </li>
                </ul>
                <div class="pagination-total">{{ itemMin }}-{{ itemMax }} of {{ itemTotal }}</div>
            </div>
        </div>
    </div>
</template>


<script>
    export default {
        data() {
            return {
                articleOverview: window.CFCU.articleOverview,
                searchString: null,
                selectedCategory: null,
                selectVisible: false,
                pageCurrent: 1,
                pageSize: 8,
                maxButtons: 0,
                totalVisible: 9,
                disabled: false,
                itemMin: 0,
                itemMax: 0,
                heroBackground: '',
                heroBackgroundMobile: '',
            }
        },
        computed: {
            filteredArticles() {
                let _articles = this.articleOverview.articles;
                if (this.searchString) {
                    _articles = _articles.filter(o =>
                        o.author.toLowerCase().includes(this.searchString.toLowerCase()) ||
                        o.authorTitle.toLowerCase().includes(this.searchString.toLowerCase()) ||
                        o.copy.toLowerCase().includes(this.searchString.toLowerCase()) ||
                        o.title.toLowerCase().includes(this.searchString.toLowerCase())
                    );
                }
                if (this.selectedCategory) {
                    _articles = _articles.filter(o => o.categories.find(c => c.name === this.selectedCategory.name))
                }

                return _articles;
            },
            paginatedArticles() {
                return this.filteredArticles.slice((this.pageCurrent - 1) * this.pageSize, this.pageCurrent * this.pageSize);
            },
            totalPages() {
                return Math.ceil(this.filteredArticles.length / this.pageSize)
            },
            isValueLast() {
                return this.pageCurrent >= this.totalPages;
            },
            isValueFirst() {
                return this.pageCurrent <= 1;
            },

            items() {
                const maxLength = this.totalVisible > this.maxButtons ? this.maxButtons : this.totalVisible;

                /*
                if (this.totalPages <= maxLength || maxLength < 1) {
                    return this.getRange(1, this.length);
                }*/
                let even = maxLength % 2 === 0 ? 1 : 0;
                let left = Math.floor(maxLength / 2);
                let right = this.totalPages - left + 1 + even;

                if (this.totalPages > 9) {
                    if (this.pageCurrent > left && this.pageCurrent < right) {
                        const start = this.pageCurrent - left + 2;
                        const end = this.pageCurrent + left - 2 - even;

                        return [1, '...', ...this.getRange(start, end), '...', this.totalPages];
                    } else if (this.pageCurrent === left) {
                        const end = this.pageCurrent + left - 1 - even;

                        return [...this.getRange(1, end), '...', this.totalPages];
                    } else if (this.pageCurrent === right) {
                        const start = this.pageCurrent - left + 1;

                        return [1, '...', ...this.getRange(start, this.totalPages)];
                    }
                    else {
                        return [...this.getRange(1, left), '...', ...this.getRange(right, this.totalPages)];
                    }
                }
                else {
                    return [...this.getRange(1, this.totalPages)];
                }
            },
            itemTotal() {
                if (this.articleOverview.articles !== null) {
                    return this.articleOverview.articles.length;
                }
                else {
                    return 0;
                }
            }
        },
        watch: {
            selectedCategory() {
                this.pageCurrent = 1
            },
            searchString() {
                this.pageCurrent = 1
            },
            pageCurrent() {
                if (this.itemTotal > 8) {
                    if (this.pageCurrent != 1) {
                        this.itemMin = ((this.pageCurrent - 1) * this.pageSize) + 1;
                    }
                    else {
                        this.itemMin = 1;
                    }

                    if (this.pageCurrent != 1) {
                        this.itemMax = ((this.pageCurrent - 1) * this.pageSize) + this.pageSize;

                        if (this.itemMax > this.itemTotal) {
                            this.itemMax = this.itemTotal
                        }
                    }
                    else {
                        this.itemMax = 8;
                    }

                    this.scrollToTop();
                }
            }
        },
        methods: {
            formatToDate(val) {
                let date = new Date(val).toLocaleDateString();
                return date.replace(/\//g, '.');
            },
            clearSelect() {
                this.selectedCategory = null;
                setTimeout(() => {
                    this.selectVisible = false;
                }, 0)
            },

            //pagination
            goNext() {
                if (this.isValueLast) return;
                this.pageCurrent = this.pageCurrent + 1;
                this.scrollToTop();
            },
            goPrevious() {
                if (this.isValueFirst) return;
                this.pageCurrent = this.pageCurrent - 1;
                this.scrollToTop();
            },
            getRange(from, to) {
                const range = [];
                from = from > 0 ? from : 1;
                for (let i = from; i <= to; i++) {
                    range.push(i);
                }
                return range;
            },
            scrollToTop() {
                let articleTop = document.querySelector('.article-overview__articles').offsetTop;
                window.scrollTo({ top: articleTop-100, left: 0, behavior: 'smooth' });
            },
            setMaxButtons() {
                const containerWidth = this.$el && this.$el.parentElement ? this.$el.parentElement.clientWidth : window.innerWidth;
                const navButton = this.$refs.navNext.getBoundingClientRect();

                // width of the items considering navItem.height = item.width
                const itemWidth = navButton.height;
                const navItemsWidth = navButton.width * 2;

                this.maxButtons = Math.floor((containerWidth - navItemsWidth) / itemWidth
                );
            },
            debouncedOnResize() {
                setTimeout(() => {
                    this.setMaxButtons();
                }, 200)
            }
        },
        mounted() {
            const params = new Proxy(new URLSearchParams(window.location.search), {
                get: (searchParams, prop) => searchParams.get(prop),
            });

            if (params.category) this.selectedCategory = this.articleOverview.articleCategories.find(el => el.name === params.category)

            this.setMaxButtons();
            window.addEventListener('resize', this.debouncedOnResize);

            if (this.itemTotal > 8) {
                this.itemMin = 1;
                this.itemMax = 8;
            }
            else {
                this.itemMin = 1;
                this.itemMax = this.itemTotal;
            }

            
            if (this.articleOverview.backgroundImageMobile != '') {
                this.heroBackground = this.articleOverview.backgroundImageMobile;
            }
            else {
                this.heroBackground = '/images/hero-internal-mobile.jpg';
            }

            if (window.outerWidth > 992) {
                if (this.articleOverview.backgroundImage != '') {
                    this.heroBackground = this.articleOverview.backgroundImage;
                }
                else {
                    this.heroBackground = '/images/hero-internal-desktop.jpg';
                }
            }
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.debouncedOnResize);
        },
    }
</script>
